import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { App as AntdApp, Button, Space, Spin } from 'antd';
import {
  DragDropReceiptEditor,
  FreeCanvasEditor,
  initPrintTemplateI18n,
  shouldUseFreeCanvas,
  type PrintTemplateDSL,
} from 'print-template-component';
import { DEMO_PREVIEW_DATA, DEMO_RECEIPT_TEMPLATE } from '../demo/template';
import {
  fetchCustomerTemplateDetail,
  fetchOpsTemplateDetail,
  fetchPresetLibrary,
  fetchPreviewMockData,
  pickSpecContent,
  publishOpsTemplate,
  setAtlasAccessToken,
  updateOpsTemplate,
  type OpsTemplateDetail,
  type OpsTemplateSpec,
  type PresetLibraryGroup,
} from '../api/opsTemplate';

export default function EditorPage() {
  const { message } = AntdApp.useApp();
  const [params] = useSearchParams();

  const opsMode = params.get('ops') === '1';
  /** 客户模板：只读打开，走 customer-templates 接口 */
  const customerMode = params.get('customer') === '1';
  const templateType = params.get('templateType') || 'CHECKOUT';
  const templateDefinitionId = params.get('templateDefinitionId') || '';
  const templateId = params.get('templateId') || params.get('id') || '';
  const paperWidthParam = params.get('paperWidth') || '';
  const groupIdParam = params.get('groupId') || '0';
  const templateScope = params.get('templateScope') || 'SYSTEM';
  const templateSource = params.get('templateSource') || 'SYSTEM';
  const locale = params.get('locale') || 'zh-CN';
  const nameFromQuery = params.get('name') || '演示模板';
  const tokenFromQuery = params.get('accessToken') || '';
  const tokenFromHash = useMemo(() => {
    const hash = window.location.hash.replace(/^#/, '');
    if (!hash) return '';
    return new URLSearchParams(hash).get('accessToken') || '';
  }, []);

  const preferredWidth = Number(paperWidthParam) || 80;

  const [loading, setLoading] = useState(opsMode);
  const [detail, setDetail] = useState<OpsTemplateDetail | null>(null);
  const [activeSpec, setActiveSpec] = useState<OpsTemplateSpec | null>(null);
  const [initialTemplate, setInitialTemplate] = useState<PrintTemplateDSL>(
    DEMO_RECEIPT_TEMPLATE as PrintTemplateDSL,
  );
  const [templateName, setTemplateName] = useState(nameFromQuery);
  const [presetGroups, setPresetGroups] = useState<PresetLibraryGroup[] | undefined>(
    undefined,
  );
  const [previewData, setPreviewData] = useState<Record<string, unknown>>(DEMO_PREVIEW_DATA);

  useEffect(() => {
    initPrintTemplateI18n(locale);
  }, [locale]);

  useEffect(() => {
    setAtlasAccessToken(tokenFromQuery || tokenFromHash || null);
  }, [tokenFromQuery, tokenFromHash]);

  useEffect(() => {
    if (!opsMode || !templateDefinitionId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = customerMode
          ? await fetchCustomerTemplateDetail(templateDefinitionId)
          : await fetchOpsTemplateDetail(templateDefinitionId);
        if (cancelled) return;
        setDetail(data);
        setTemplateName(data?.templateName || nameFromQuery);
        const paletteType = data?.templateType || templateType;
        const [library, mock] = await Promise.all([
          fetchPresetLibrary(paletteType).catch(() => [] as PresetLibraryGroup[]),
          fetchPreviewMockData(paletteType).catch(() => null),
        ]);
        if (cancelled) return;
        setPresetGroups(library?.length ? library : undefined);
        if (mock && Object.keys(mock).length > 0) {
          setPreviewData(mock);
        }
        const { dsl, spec } = pickSpecContent(data, {
          preferredWidth,
          templateId,
        });
        setActiveSpec(spec);
        if (dsl && typeof dsl === 'object') {
          setInitialTemplate(dsl as PrintTemplateDSL);
        }
      } catch (e) {
        message.error(
          customerMode
            ? `加载客户模板失败: ${(e as Error).message}`
            : `加载系统默认模板失败: ${(e as Error).message}`,
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    opsMode,
    customerMode,
    templateDefinitionId,
    templateId,
    templateType,
    preferredWidth,
    nameFromQuery,
    message,
    tokenFromQuery,
    tokenFromHash,
  ]);

  const resolvedType = detail?.templateType || templateType;
  const useFree = shouldUseFreeCanvas(resolvedType);
  const activeTemplateId = String(activeSpec?.templateId ?? templateId);
  const activePaperWidth = activeSpec?.paperWidth ?? preferredWidth;

  const handleSave = useCallback(
    async (template: unknown, name: string) => {
      if (!opsMode) {
        console.info('[print-template-app] save', {
          templateId,
          templateType,
          name,
          template,
        });
        message.success(`已保存本地草稿：${name}`);
        return;
      }
      if (customerMode) {
        message.warning('客户模板暂不支持在运营端修改');
        return;
      }
      if (!templateDefinitionId || !detail) {
        message.error('缺少模板定义，无法保存');
        return;
      }
      const contentJson = JSON.stringify(template);
      const existing = detail.templates ?? [];
      const targetId = activeTemplateId;
      const targetWidth = activePaperWidth;

      const templates =
        existing.length > 0
          ? existing.map((spec) => {
              const matched = targetId
                ? String(spec.templateId) === targetId
                : spec.paperWidth === targetWidth;
              return {
                templateId: spec.templateId,
                paperWidth: spec.paperWidth,
                contentJson: matched ? contentJson : spec.contentJson,
                previewImage: spec.previewImage,
                isDefault: 1,
              };
            })
          : [{ paperWidth: targetWidth || 80, contentJson, isDefault: 1 }];

      try {
        await updateOpsTemplate(templateDefinitionId, {
          templateDefinitionId: detail.templateDefinitionId ?? templateDefinitionId,
          groupId: detail.groupId ?? groupIdParam ?? 0,
          shopId: detail.shopId ?? null,
          templateType: detail.templateType || templateType,
          templateName: name || detail.templateName || nameFromQuery,
          templateScope: detail.templateScope || templateScope,
          templateSource: detail.templateSource || templateSource,
          templates,
        });
        message.success(`已保存系统默认模板：${name}`);
        const refreshed = await fetchOpsTemplateDetail(templateDefinitionId);
        setDetail(refreshed);
        setTemplateName(refreshed?.templateName || name);
        const picked = pickSpecContent(refreshed, {
          preferredWidth: targetWidth,
          templateId: targetId,
        });
        setActiveSpec(picked.spec);
      } catch (e) {
        message.error(`保存失败: ${(e as Error).message}`);
      }
    },
    [
      opsMode,
      customerMode,
      templateId,
      templateType,
      templateDefinitionId,
      detail,
      message,
      activeTemplateId,
      activePaperWidth,
      groupIdParam,
      templateScope,
      templateSource,
      nameFromQuery,
    ],
  );

  const handlePublish = useCallback(async () => {
    if (customerMode) {
      message.warning('客户模板暂不支持在运营端发布');
      return;
    }
    if (!templateDefinitionId) return;
    try {
      await publishOpsTemplate(templateDefinitionId);
      message.success('已发布系统默认模板');
    } catch (e) {
      message.error(`发布失败: ${(e as Error).message}`);
    }
  }, [customerMode, templateDefinitionId, message]);

  const handleCancel = () => {
    if (opsMode) {
      window.close();
      // 部分浏览器在非脚本打开的标签上会拦截 close，给个兜底提示
      window.setTimeout(() => {
        message.info('请关闭此标签页返回运营平台');
      }, 200);
      return;
    }
    message.info('已取消编辑');
  };

  const editorKey = useMemo(
    () => `ops-${templateDefinitionId}-${activeTemplateId}-${activePaperWidth}`,
    [templateDefinitionId, activeTemplateId, activePaperWidth],
  );

  if (loading) {
    return (
      <div
        className="app-shell"
        style={{ display: 'grid', placeItems: 'center', minHeight: '60vh' }}
      >
        <Spin tip={customerMode ? '加载客户模板...' : '加载系统默认模板...'} />
      </div>
    );
  }

  return (
    <div className="app-shell">
      {opsMode && (
        <div style={{ padding: '8px 16px', borderBottom: '1px solid #eee' }}>
          <Space>
            <span style={{ whiteSpace: 'nowrap' }}>
              {`${customerMode ? '客户只读' : '运维编辑'} · ${templateName} · ${resolvedType} · ${activePaperWidth}mm${activeTemplateId ? ` · #${activeTemplateId}` : ''}`}
            </span>
            {!customerMode && (
              <Button type="primary" onClick={handlePublish}>
                发布
              </Button>
            )}
          </Space>
        </div>
      )}
      {useFree ? (
        <FreeCanvasEditor
          key={editorKey}
          initialName={templateName}
          initialTemplate={initialTemplate as never}
          previewData={previewData}
          presetGroups={presetGroups as never}
          readOnly={customerMode}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      ) : (
        <DragDropReceiptEditor
          key={editorKey}
          initialTemplate={initialTemplate}
          initialName={templateName}
          previewData={previewData}
          presetGroups={presetGroups as never}
          readOnly={customerMode}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      )}
    </div>
  );
}
